/**
 * Taking a customer or a session out of the working lists, without destroying it.
 *
 * Two intents, one mechanism. Archive is "old, keep it": a customer we no longer
 * work with, a workshop that ran last year. Delete is "this never happened": a
 * session created by mistake, a duplicate customer. Both are soft — nothing in
 * the app calls `deleteDoc` on a customer or a session, and `firestore.rules`
 * refuses one outright.
 *
 * What separates them for a reader is that only an ARCHIVE is undone by the app.
 * The write is identical either way and `restoreCustomer` / `restoreSession`
 * would clear either one — but the Archive offers Restore on one shelf only, so
 * putting a deleted record back takes database access. Delete is therefore the
 * heavier press, and the copy in `content/archive.ts` says so.
 *
 * ONE stamp and ONE kind, not a boolean per intent. "Archived and deleted" is
 * not a state anything could render, so it must not be a state anything can
 * write; a pair of flags makes it representable and then every reader has to
 * decide which one wins.
 */
export const REMOVAL_KINDS = ["archived", "deleted"] as const

export type RemovalKind = (typeof REMOVAL_KINDS)[number]

/**
 * What every removable document carries. Mixed into `Customer` and `Session`
 * rather than kept in a nested map: `updateDoc` replaces a nested map wholesale,
 * so a partial patch of one would silently drop the other two fields.
 *
 * All three are null on a record that is present. A document written before
 * removal existed has none of them, which the snapshot mappers read as exactly
 * that.
 */
export interface Removable {
  /**
   * When it was taken out, as epoch ms. A `serverTimestamp()`, so it reads back
   * null for one round trip — which is why nothing derives the STATE from it.
   * It orders the Archive and nothing else; `pendingAsNow` covers the gap.
   */
  removedAt: number | null
  /** Which of the two intents. Null means the record is present. */
  removedAs: RemovalKind | null
  /** The facilitator who did it, by uid. Shown in the Archive so it is answerable. */
  removedBy: string | null
}

/** The zero value, written at create so every document has the same shape. */
export const NOT_REMOVED: Removable = {
  removedAt: null,
  removedAs: null,
  removedBy: null,
}
